import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { passTypesApi } from '@/shared/api';
import type {
  GetPassTypesResponse,
  GetPassTypeResponse,
  CreatePassTypeRequest,
  CreatePassTypeResponse,
  UpdatePassTypeRequest,
  UpdatePassTypeResponse,
  DeletePassTypeResponse,
} from '@/shared/api';
import { emitToast } from '@/shared/lib/toastEvents';
import { passTypesKeys } from '@/shared/lib/queryKeys';

export { passTypesKeys };

export const passTypesQueryOptions = {
  list: () =>
    queryOptions<GetPassTypesResponse>({
      queryKey: passTypesKeys.list(),
      queryFn: ({ signal }) => passTypesApi.getAll({ signal }),
      meta: { errorToast: 'Failed to load pass templates. Contact support.' },
    }),
  detail: (passTypeId: string) =>
    queryOptions<GetPassTypeResponse>({
      queryKey: passTypesKeys.byId(passTypeId),
      queryFn: ({ signal }) => passTypesApi.getById(passTypeId, { signal }),
      meta: { errorToast: 'Failed to load pass template. Contact support.' },
    }),
};

// Mutations
export function useCreatePassType(
  options?: UseMutationOptions<CreatePassTypeResponse, Error, CreatePassTypeRequest, unknown>
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to create pass template. Contact support.', ...options?.meta },
    mutationFn: (data: CreatePassTypeRequest) => passTypesApi.create(data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Pass template created: ${variables.name}`,
      });
      await queryClient.invalidateQueries({ queryKey: passTypesKeys.list() });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useUpdatePassType(
  options?: UseMutationOptions<
    UpdatePassTypeResponse,
    Error,
    { id: string; data: UpdatePassTypeRequest },
    unknown
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to update pass template. Contact support.', ...options?.meta },
    mutationFn: ({ id, data }: { id: string; data: UpdatePassTypeRequest }) =>
      passTypesApi.update(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Pass template updated: ${variables.data.name}`,
      });
      await queryClient.invalidateQueries({ queryKey: passTypesKeys.byId(variables.id) });
      await queryClient.invalidateQueries({ queryKey: passTypesKeys.list() });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useDeletePassType(
  options?: UseMutationOptions<
    DeletePassTypeResponse,
    Error,
    { id: string; name?: string },
    unknown
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to delete pass template. Contact support.', ...options?.meta },
    mutationFn: ({ id }) => passTypesApi.delete(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Pass template deleted${variables.name ? `: ${variables.name}` : ''}`,
      });
      queryClient.removeQueries({ queryKey: passTypesKeys.byId(variables.id) });
      await queryClient.invalidateQueries({ queryKey: passTypesKeys.list() });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
